import {
  ZadCustomerReturn,
  ZadCustomerReturnItem,
  ZadCustomerReturnItemWritePayload,
  ZadCustomerReturnStatus,
  ZadCustomerReturnUpdatePayload,
  ZadCustomerReturnWritePayload,
  ZadReturnableItem,
  ZadSale,
  ZadSaleReturnState,
  ZadSaleReturnSummary,
  ZadSaleReturnable,
} from '../../../models/zad'
import {getZadCustomerDisplayName} from './customerHelpers'
import {ZadApiError, resolveZadUserMessage} from './errors'
import {roundZadMoney3} from './productHelpers'
import {getZadSaleCustomerId, getZadSaleCustomerLabel} from './saleHelpers'
import {parseZadStockQuantity} from './stockHelpers'

export const ZAD_CUSTOMER_RETURN_STATUS_I18N: Record<ZadCustomerReturnStatus, string> = {
  DRAFT: 'ZAD.CUSTOMER_RETURNS.STATUS_DRAFT',
  VALIDATED: 'ZAD.CUSTOMER_RETURNS.STATUS_VALIDATED',
  CANCELLED: 'ZAD.CUSTOMER_RETURNS.STATUS_CANCELLED',
}

export type ZadCustomerReturnEditorItem = {
  saleItemId: string
  productName: string
  productCode: string
  barcode?: string
  saleUnit?: string
  packagingLabelSnapshot?: string | null
  soldQuantity: number
  alreadyReturned: number
  returnableQuantity: number
  previewUnitReturnValue: number
  quantity: string
  reason: string
}

export type ZadCustomerReturnEditorValues = {
  sale: string
  returnDate: string
  reason: string
  notes: string
  items: ZadCustomerReturnEditorItem[]
}

export type ZadCustomerReturnPreview = {
  itemCount: number
  totalQuantity: number
  totalReturnValue: number
}

export type ZadReturnMoneyKind = 'NO_REFUND_DUE' | 'REFUND_DUE' | 'PARTIAL' | 'REFUNDED'

export function previouslyReturnedQuantityOf(
  item?: Pick<ZadReturnableItem, 'previouslyReturnedQuantity' | 'alreadyReturned'> | Pick<
    ZadCustomerReturnItem,
    'previouslyReturnedQuantity' | 'alreadyReturned'
  > | null
): number {
  if (!item) return 0
  if (item.previouslyReturnedQuantity != null) return Number(item.previouslyReturnedQuantity) || 0
  if (item.alreadyReturned != null) return Number(item.alreadyReturned) || 0
  return 0
}

export function historicalUnitValueOf(item?: {
  historicalEffectiveUnitValue?: number
  previewUnitReturnValue?: number
  unitReturnValue?: number
} | null): number {
  if (!item) return 0
  if (item.historicalEffectiveUnitValue != null) return Number(item.historicalEffectiveUnitValue) || 0
  if (item.previewUnitReturnValue != null) return Number(item.previewUnitReturnValue) || 0
  if (item.unitReturnValue != null) return Number(item.unitReturnValue) || 0
  return 0
}

export function currentReturnQtyOf(item?: Pick<ZadCustomerReturnItem, 'currentReturnQuantity' | 'quantity'> | null): number {
  if (!item) return 0
  if (item.currentReturnQuantity != null) return Number(item.currentReturnQuantity) || 0
  return Number(item.quantity) || 0
}

export function currentReturnValueOf(
  item?: Pick<ZadCustomerReturnItem, 'currentReturnValue' | 'lineReturnValue'> | null,
  quantity?: number,
  unitValue?: number
): number {
  if (item?.currentReturnValue != null) return Number(item.currentReturnValue) || 0
  if (quantity == null && item?.lineReturnValue != null) return Number(item.lineReturnValue) || 0
  if (quantity != null && unitValue != null) return previewReturnLineValue(quantity, unitValue)
  if (item?.lineReturnValue != null) return Number(item.lineReturnValue) || 0
  return 0
}

export function returnTotalOf(ret?: Pick<ZadCustomerReturn, 'returnTotal' | 'totalReturnValue'> | null): number {
  if (!ret) return 0
  if (ret.returnTotal != null) return Number(ret.returnTotal) || 0
  return Number(ret.totalReturnValue) || 0
}

export function stockQuantityRestoredOf(
  ret?: Pick<ZadCustomerReturn, 'status' | 'items' | 'stockQuantityRestored'> | null
): number {
  if (!ret) return 0
  if (ret.stockQuantityRestored != null) return Number(ret.stockQuantityRestored) || 0
  if (ret.status !== 'VALIDATED') return 0
  return (ret.items || []).reduce((sum, item) => sum + currentReturnQtyOf(item), 0)
}

export function refundedAmountOf(ret?: Pick<ZadCustomerReturn, 'refundedAmount'> | null): number {
  if (ret?.refundedAmount == null) return 0
  return Number(ret.refundedAmount) || 0
}

export function remainingRefundableAmountOf(
  ret?: Pick<ZadCustomerReturn, 'remainingRefundableAmount'> | null
): number {
  if (ret?.remainingRefundableAmount == null) return 0
  return Number(ret.remainingRefundableAmount) || 0
}

export function classifyZadReturnMoney(
  ret?: Pick<ZadCustomerReturn, 'moneyStatus' | 'refundedAmount' | 'remainingRefundableAmount' | 'status'> | Partial<
    Pick<ZadCustomerReturn, 'moneyStatus' | 'refundedAmount' | 'remainingRefundableAmount' | 'status'>
  > | null
): ZadReturnMoneyKind {
  const remaining = remainingRefundableAmountOf(ret)
  const refunded = refundedAmountOf(ret)
  const raw = String(ret?.moneyStatus || '').toUpperCase()
  if (raw === 'PARTIAL' || raw === 'PARTIALLY_REFUNDED') return 'PARTIAL'
  if (raw === 'REFUNDED' || raw === 'FULLY_REFUNDED' || raw === 'COMPLETE') return 'REFUNDED'
  if (raw === 'REFUND_DUE' || raw === 'DUE' || raw === 'TO_REFUND') return 'REFUND_DUE'
  if (raw === 'NO_REFUND_DUE' || raw === 'NONE' || raw === 'NO_REFUND') return 'NO_REFUND_DUE'
  if (remaining > 0 && refunded > 0) return 'PARTIAL'
  if (remaining > 0) return 'REFUND_DUE'
  if (refunded > 0) return 'REFUNDED'
  return 'NO_REFUND_DUE'
}

export function saleReturnStateOf(summary?: ZadSaleReturnSummary | null): ZadSaleReturnState {
  const raw = String(
    summary?.returnState || summary?.returnStatus || summary?.status || 'NONE'
  ).toUpperCase()
  if (raw === 'PARTIAL') return 'PARTIAL'
  if (raw === 'FULL') return 'FULL'
  if ((Number(summary?.returnedQuantity) || 0) > 0) return 'PARTIAL'
  return 'NONE'
}

export function saleLineRemainingQty(item: {
  quantity: number
  returnedQuantity?: number
  remainingReturnableQuantity?: number
}): number | null {
  if (item.remainingReturnableQuantity != null) return Number(item.remainingReturnableQuantity)
  if (item.returnedQuantity != null) return Math.max(0, Number(item.quantity) - Number(item.returnedQuantity))
  return null
}

export function listZadReturnStockLabel(ret: ZadCustomerReturn): {kind: 'none' | 'plus'; qty: number} {
  if (ret.status !== 'VALIDATED') return {kind: 'none', qty: 0}
  return {kind: 'plus', qty: stockQuantityRestoredOf(ret)}
}

export function listZadReturnMoneyKind(ret: ZadCustomerReturn): ZadReturnMoneyKind | 'UNKNOWN' {
  if (ret.status !== 'VALIDATED') return 'UNKNOWN'
  if (ret.remainingRefundableAmount == null && ret.refundedAmount == null && !ret.moneyStatus) {
    return 'UNKNOWN'
  }
  return classifyZadReturnMoney(ret)
}

export function todayZadIsoDate(): string {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`
}

export function canEditZadCustomerReturn(status?: ZadCustomerReturnStatus | string): boolean {
  return status === 'DRAFT'
}

export function canValidateZadCustomerReturn(status?: ZadCustomerReturnStatus | string): boolean {
  return status === 'DRAFT'
}

export function canCancelZadCustomerReturn(status?: ZadCustomerReturnStatus | string): boolean {
  return status === 'DRAFT'
}

export function canCreateZadSaleReturn(sale?: Pick<ZadSale, 'status'> | null): boolean {
  return sale?.status === 'VALIDATED'
}

export function emptyZadCustomerReturnEditorValues(): ZadCustomerReturnEditorValues {
  return {
    sale: '',
    returnDate: todayZadIsoDate(),
    reason: '',
    notes: '',
    items: [],
  }
}

export function parseZadReturnQuantity(
  value: string | number | null | undefined
): {ok: true; value: number} | {ok: false; errorId: string} {
  const parsed = parseZadStockQuantity(value)
  if (parsed.ok) return parsed
  if (parsed.errorId === 'ZAD.STOCK.QUANTITY_ZERO') {
    return {ok: false, errorId: 'ZAD.CUSTOMER_RETURNS.QTY_ZERO'}
  }
  if (parsed.errorId === 'ZAD.STOCK.QUANTITY_NEGATIVE') {
    return {ok: false, errorId: 'ZAD.CUSTOMER_RETURNS.QTY_NEGATIVE'}
  }
  if (parsed.errorId === 'ZAD.STOCK.QUANTITY_INTEGER') {
    return {ok: false, errorId: 'ZAD.CUSTOMER_RETURNS.QTY_INTEGER'}
  }
  return {ok: false, errorId: 'ZAD.CUSTOMER_RETURNS.QTY_REQUIRED'}
}

export function previewReturnLineValue(
  quantity: number,
  previewUnitReturnValue: number
): number {
  if (quantity <= 0) return 0
  return roundZadMoney3(previewUnitReturnValue * quantity)
}

export function mapReturnableItemsToEditor(
  items: ZadReturnableItem[],
  existing?: Array<{saleItemId: string; quantity?: number | string; reason?: string}>
): ZadCustomerReturnEditorItem[] {
  const existingById = new Map(
    (existing || []).map((row) => [String(row.saleItemId), row])
  )
  return items.map((item) => {
    const current = existingById.get(String(item.saleItemId))
    const parsedExisting = current?.quantity == null || current.quantity === ''
      ? null
      : parseZadReturnQuantity(current.quantity)
    const qty = parsedExisting?.ok ? String(parsedExisting.value) : ''
    return {
      saleItemId: String(item.saleItemId),
      productName: item.productName || '',
      productCode: item.productCode || '',
      barcode: item.barcode || '',
      saleUnit: item.saleUnit ? String(item.saleUnit) : undefined,
      packagingLabelSnapshot: item.packagingLabelSnapshot || null,
      soldQuantity: item.soldQuantity,
      alreadyReturned: previouslyReturnedQuantityOf(item),
      returnableQuantity: item.returnableQuantity,
      previewUnitReturnValue: historicalUnitValueOf(item),
      quantity: qty,
      reason: current?.reason || '',
    }
  })
}

export function previewZadCustomerReturn(
  items: ZadCustomerReturnEditorItem[]
): ZadCustomerReturnPreview {
  let itemCount = 0
  let totalQuantity = 0
  let total = 0
  for (const row of items) {
    if (!row.quantity) continue
    const parsed = parseZadReturnQuantity(row.quantity)
    if (!parsed.ok) continue
    itemCount += 1
    totalQuantity += parsed.value
    total = roundZadMoney3(
      total + previewReturnLineValue(parsed.value, row.previewUnitReturnValue)
    )
  }
  return {itemCount, totalQuantity, totalReturnValue: total}
}

export function fillZadReturnableQty(row: ZadCustomerReturnEditorItem): string {
  if (row.returnableQuantity <= 0) return ''
  return String(row.returnableQuantity)
}

export function selectedZadReturnItems(
  items: ZadCustomerReturnEditorItem[]
): ZadCustomerReturnItemWritePayload[] {
  const selected: ZadCustomerReturnItemWritePayload[] = []
  const seen = new Set<string>()
  for (const row of items) {
    if (!row.quantity) continue
    const parsed = parseZadReturnQuantity(row.quantity)
    if (!parsed.ok) continue
    if (seen.has(row.saleItemId)) continue
    seen.add(row.saleItemId)
    selected.push({
      saleItemId: row.saleItemId,
      quantity: parsed.value,
      reason: String(row.reason || '').trim(),
    })
  }
  return selected
}

export function validateZadCustomerReturnForm(
  values: ZadCustomerReturnEditorValues
): string | null {
  if (!String(values.sale || '').trim()) return 'ZAD.CUSTOMER_RETURNS.SALE_REQUIRED'
  if (!String(values.returnDate || '').trim()) return 'ZAD.CUSTOMER_RETURNS.DATE_REQUIRED'
  const seen = new Set<string>()
  let selected = 0
  for (const row of values.items) {
    if (!row.quantity) continue
    const parsed = parseZadReturnQuantity(row.quantity)
    if (!parsed.ok) return parsed.errorId
    if (parsed.value > row.returnableQuantity) {
      return 'ZAD.CUSTOMER_RETURNS.QTY_EXCEEDS'
    }
    if (seen.has(row.saleItemId)) return 'ZAD.CUSTOMER_RETURNS.DUPLICATE_ITEM'
    seen.add(row.saleItemId)
    selected += 1
  }
  if (selected === 0) return 'ZAD.CUSTOMER_RETURNS.ITEMS_REQUIRED'
  return null
}

export function sanitizeZadCustomerReturnWritePayload(
  payload: ZadCustomerReturnWritePayload
): ZadCustomerReturnWritePayload {
  return {
    sale: String(payload.sale || '').trim(),
    returnDate: String(payload.returnDate || '').trim(),
    reason: String(payload.reason || '').trim(),
    notes: String(payload.notes || '').trim(),
    items: (payload.items || []).map((row) => ({
      saleItemId: String(row.saleItemId || '').trim(),
      quantity: Number(row.quantity) || 0,
      reason: String(row.reason || '').trim(),
    })),
  }
}

export function sanitizeZadCustomerReturnUpdatePayload(
  payload: ZadCustomerReturnUpdatePayload
): ZadCustomerReturnUpdatePayload {
  return {
    returnDate: String(payload.returnDate || '').trim(),
    reason: String(payload.reason || '').trim(),
    notes: String(payload.notes || '').trim(),
    items: (payload.items || []).map((row) => ({
      saleItemId: String(row.saleItemId || '').trim(),
      quantity: Number(row.quantity) || 0,
      reason: String(row.reason || '').trim(),
    })),
  }
}

export function toZadCustomerReturnPayload(
  values: ZadCustomerReturnEditorValues
): ZadCustomerReturnWritePayload {
  return {
    sale: String(values.sale || '').trim(),
    returnDate: String(values.returnDate || '').trim(),
    reason: String(values.reason || '').trim(),
    notes: String(values.notes || '').trim(),
    items: selectedZadReturnItems(values.items),
  }
}

export function toZadCustomerReturnUpdatePayload(
  values: ZadCustomerReturnEditorValues
): ZadCustomerReturnUpdatePayload {
  const {sale: _sale, ...rest} = toZadCustomerReturnPayload(values)
  return rest
}

export function getZadCustomerReturnSaleId(
  sale: ZadCustomerReturn['sale'] | string | null | undefined
): string {
  if (!sale) return ''
  if (typeof sale === 'string') return sale
  return sale._id || ''
}

export function getZadCustomerReturnSaleNumber(ret?: ZadCustomerReturn | null): string {
  if (!ret) return '—'
  if (ret.saleNumber) return ret.saleNumber
  if (ret.sale && typeof ret.sale !== 'string') return ret.sale.saleNumber || '—'
  return '—'
}

export function formatZadCustomerReturnCustomer(
  ret?: Pick<ZadCustomerReturn, 'customer' | 'customerSnapshot'> | null
): string {
  if (!ret) return ''
  if (ret.customerSnapshot?.displayName) return ret.customerSnapshot.displayName
  if (ret.customer && typeof ret.customer !== 'string') {
    return getZadCustomerDisplayName(ret.customer)
  }
  return ''
}

export function isWalkInZadCustomerReturn(
  ret?: Pick<ZadCustomerReturn, 'customer'> | null
): boolean {
  if (!ret) return true
  if (!ret.customer) return true
  if (typeof ret.customer === 'string') return !ret.customer
  return !ret.customer._id
}

export function getZadCustomerReturnCustomerId(
  ret?: Pick<ZadCustomerReturn, 'customer'> | null
): string {
  if (!ret?.customer) return ''
  if (typeof ret.customer === 'string') return ret.customer
  return ret.customer._id || ''
}

export function formatZadReturnSaleCustomer(sale?: ZadSale | null): string {
  if (!sale) return ''
  return getZadSaleCustomerLabel(sale)
}

export function buildZadCustomerReturnNewPath(saleId?: string): string {
  return saleId
    ? `/zad/customer-returns/new?sale=${encodeURIComponent(saleId)}`
    : '/zad/customer-returns/new'
}

export function incrementZadReturnQty(current: string, max: number): string {
  if (max <= 0) return ''
  const parsed = parseZadReturnQuantity(current)
  const next = parsed.ok ? parsed.value + 1 : 1
  return String(Math.min(next, max))
}

export function decrementZadReturnQty(current: string): string {
  const parsed = parseZadReturnQuantity(current)
  if (!parsed.ok) return ''
  const next = parsed.value - 1
  return next > 0 ? String(next) : ''
}

export function formatZadReturnExceededDetails(err: ZadApiError): {
  productName?: string
  productCode?: string
  soldQuantity?: number
  alreadyReturned?: number
  returnable?: number
  requested?: number
} {
  return {
    productName: err.details?.productName,
    productCode: err.details?.productCode,
    soldQuantity: err.details?.soldQuantity,
    alreadyReturned: err.details?.previouslyReturnedQuantity ?? err.details?.alreadyReturned,
    returnable: err.details?.returnable,
    requested: err.details?.requested,
  }
}

export function resolveZadCustomerReturnMessage(
  err: ZadApiError
): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    VALIDATION_ERROR: 'ZAD.CUSTOMER_RETURNS.VALIDATION_ERROR',
    RETURN_NOT_FOUND: 'ZAD.CUSTOMER_RETURNS.NOT_FOUND',
    RETURN_NOT_DRAFT: 'ZAD.CUSTOMER_RETURNS.NOT_DRAFT',
    RETURN_ALREADY_VALIDATED: 'ZAD.CUSTOMER_RETURNS.ALREADY_VALIDATED',
    RETURN_CANCELLED: 'ZAD.CUSTOMER_RETURNS.CANCELLED',
    SALE_NOT_FOUND: 'ZAD.SALES.NOT_FOUND',
    SALE_NOT_VALIDATED: 'ZAD.CUSTOMER_RETURNS.SALE_NOT_VALIDATED',
    SALE_ITEM_NOT_FOUND: 'ZAD.CUSTOMER_RETURNS.SALE_ITEM_NOT_FOUND',
    DUPLICATE_RETURN_ITEM: 'ZAD.CUSTOMER_RETURNS.DUPLICATE_ITEM',
    RETURN_QUANTITY_EXCEEDED: 'ZAD.CUSTOMER_RETURNS.QTY_EXCEEDED',
    INVALID_QUANTITY: 'ZAD.CUSTOMER_RETURNS.QTY_INTEGER',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}

export function saleReturnableHasCustomer(returnable?: ZadSaleReturnable | null): boolean {
  return Boolean(returnable?.customer)
}

export function saleHasRegisteredCustomer(sale?: Pick<ZadSale, 'customer'> | null): boolean {
  return Boolean(getZadSaleCustomerId(sale?.customer))
}
